import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEO = ({ 
  title = 'Borsamex',
  description = 'Yapay zeka destekli trading sinyalleri ile doğru zamanda doğru kararlar. Ne zaman AL, ne zaman SAT - biz söyleriz.',
  keywords = '',
  image = '/og-image.png',
  url = '',
  type = 'website',
  noindex = false,
  structuredData = null
}) => {
  const siteTitle = 'Borsamex';
  const fullTitle = title === siteTitle ? title : `${title} | ${siteTitle}`;
  const baseUrl = 'https://borsamex.com';
  const fullUrl = url ? `${baseUrl}${url}` : baseUrl;
  const fullImage = image.startsWith('http') ? image : `${baseUrl}${image}`;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      
      {/* Robots */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}
      
      {/* Canonical URL */}
      <link rel="canonical" href={fullUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:site_name" content={siteTitle} />
      <meta property="og:locale" content="tr_TR" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />
      
      {/* Structured Data - supports single object or array */}
      {structuredData && (
        Array.isArray(structuredData) 
          ? structuredData.map((data, index) => (
              <script key={index} type="application/ld+json">
                {JSON.stringify(data)}
              </script>
            ))
          : (
              <script type="application/ld+json">
                {JSON.stringify(structuredData)}
              </script>
            )
      )}
    </Helmet>
  );
};

// Pre-defined SEO configs for NEW structure pages
export const SEOConfigs = {
  home: {
    title: 'AI Trading Sinyalleri | Ne Zaman AL, Ne Zaman SAT',
    description: 'Yapay zeka destekli trading sinyalleri ile doğru zamanda doğru kararlar. Kripto ve borsa için anlık AL/SAT sinyalleri. Ücretsiz başla!',
    keywords: 'trading sinyal, kripto sinyal, al sat sinyali, yapay zeka trading, ai sinyal, borsa sinyal, bitcoin sinyal, ethereum sinyal, anlık sinyal, trading bildirimi',
    url: '/',
    structuredData: [
      // Organization Schema
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "Borsamex",
        "url": "https://borsamex.com",
        "logo": "https://borsamex.com/logo512.png",
        "description": "Türkiye'nin AI destekli kripto trading sinyal platformu",
        "foundingDate": "2024",
        "contactPoint": {
          "@type": "ContactPoint",
          "contactType": "customer service",
          "email": "info@borsamex.com",
          "availableLanguage": "Turkish"
        },
        "sameAs": [
          "https://twitter.com/borsamex",
          "https://t.me/BorsamexSignalBot"
        ]
      },
      // WebSite Schema with SearchAction
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "Borsamex",
        "url": "https://borsamex.com",
        "description": "AI destekli kripto trading sinyalleri",
        "inLanguage": "tr-TR",
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://borsamex.com/search?q={search_term_string}",
          "query-input": "required name=search_term_string"
        }
      },
      // SoftwareApplication Schema
      {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "Borsamex",
        "applicationCategory": "FinanceApplication",
        "operatingSystem": "Web",
        "description": "Yapay zeka destekli kripto trading sinyal platformu. 7/24 piyasa taraması, anlık LONG/SHORT sinyalleri.",
        "url": "https://borsamex.com",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "TRY",
          "description": "Ücretsiz plan ile başlayın"
        },
        "featureList": [
          "7/24 Piyasa Taraması",
          "AI Destekli Analiz",
          "LONG/SHORT Sinyaller",
          "Telegram Bildirimleri",
          "Risk Yönetimi (TP/SL)"
        ]
      },
      // FAQ Schema - Ana Sayfa
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "Borsamex borsama bağlanıyor mu?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Hayır. Borsamex sadece sinyal verir. Borsana bağlanmıyoruz, API anahtarı istemiyoruz, paranıza dokunmuyoruz. İşlemi kendi hesabında kendin yaparsın."
            }
          },
          {
            "@type": "Question",
            "name": "Sinyaller yatırım tavsiyesi mi?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Hayır. Sinyaller teknik analize dayalı fırsat bildirimleridir, yatırım tavsiyesi değildir. Her işlemde kendi araştırmanı yap ve risk yönetimini uygula."
            }
          },
          {
            "@type": "Question",
            "name": "Telegram'dan bildirim geliyor mu?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Evet. Pro üyelik ile anlık Telegram bildirimi alabilirsin. Sinyal üretildiği anda telefonuna mesaj düşer."
            }
          },
          {
            "@type": "Question",
            "name": "TP ve Stop ne demek?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "TP (Take Profit) = Kar al seviyesi. Stop (Stop Loss) = Zarar kes seviyesi. Her sinyalde bu seviyeler belirlenir, böylece ne zaman çıkacağını önceden bilirsin."
            }
          },
          {
            "@type": "Question",
            "name": "Ücretsiz kullanım var mı?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Evet. Ücretsiz plan ile tüm canlı sinyalleri görebilir, geçmiş performansı inceleyebilirsin. Anlık bildirimler için Pro üyelik gerekir."
            }
          }
        ]
      }
    ]
  },
  nasilCalisir: {
    title: 'Nasıl Çalışır? | AI Trading Sinyalleri',
    description: 'Borsamex nasıl çalışır? Yapay zeka piyasaları analiz eder, sinyal üretir, siz karar verirsiniz. 3 adımda trading sinyallerine başlayın.',
    keywords: 'trading sinyal nasıl çalışır, ai sinyal sistemi, otomatik analiz, piyasa taraması, sinyal bildirimi, trading nasıl yapılır',
    url: '/nasil-calisir'
  },
  pricing: {
    title: 'Fiyatlandırma | Ücretsiz ve Pro Planlar',
    description: 'Borsamex fiyatlandırma planları. Ücretsiz plan ile sinyalleri gör, Pro plan ile anlık bildirim al. Telegram, Push ve E-posta bildirimleri.',
    keywords: 'trading sinyal fiyat, kripto sinyal ücreti, borsamex fiyat, pro üyelik, sinyal aboneliği, telegram sinyal',
    url: '/pricing'
  },
  hakkimizda: {
    title: 'Hakkımızda | Borsamex',
    description: 'Borsamex: Trading\'de zamanlamayı yapay zekaya bırak. Türkiye\'nin AI destekli trading sinyal platformu. Şeffaf, güvenilir, yerli.',
    keywords: 'borsamex hakkında, ai trading platformu, türk trading platformu, yapay zeka sinyal, güvenilir sinyal',
    url: '/kurumsal/hakkimizda'
  },
  iletisim: {
    title: 'İletişim | Borsamex',
    description: 'Borsamex ile iletişime geçin. Sorularınız, önerileriniz ve iş birliği teklifleri için bize ulaşın.',
    keywords: 'borsamex iletişim, destek, yardım, soru',
    url: '/kurumsal/iletisim'
  },
  sss: {
    title: 'Sıkça Sorulan Sorular (SSS) | Borsamex',
    description: 'Borsamex hakkında merak ettikleriniz. AI trading sinyalleri, üyelik planları, bildirimler ve daha fazlası hakkında yanıtlar.',
    keywords: 'borsamex sss, sıkça sorulan sorular, trading sinyal yardım, kripto sinyal destek, borsamex nasıl çalışır, faq',
    url: '/sss'
  },
  login: {
    title: 'Giriş Yap',
    description: 'Borsamex hesabınıza giriş yapın ve AI trading sinyallerini takip etmeye başlayın.',
    url: '/login',
    noindex: true
  },
  register: {
    title: 'Ücretsiz Kayıt Ol',
    description: 'Borsamex\'e ücretsiz kayıt olun ve AI trading sinyallerini almaya başlayın. Kredi kartı gerekmez!',
    keywords: 'kayıt ol, ücretsiz hesap, trading sinyal başla, borsamex kayıt',
    url: '/register'
  },
  // Old pages - marked as noindex
  marketplace: { noindex: true },
  trader: { noindex: true },
  seller: { noindex: true },
  partner: { noindex: true },
  subscriber: { noindex: true },
  rehber: { noindex: true },
  dashboard: { noindex: true }
};

// Structured data for homepage
export const HomeStructuredData = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "Borsamex",
  "applicationCategory": "FinanceApplication",
  "operatingSystem": "Web",
  "description": "Yapay zeka destekli trading sinyalleri platformu. Kripto ve borsa için anlık AL/SAT sinyalleri.",
  "url": "https://borsamex.com",
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "TRY",
    "description": "Ücretsiz plan ile başlayın"
  },
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.8",
    "ratingCount": "150"
  }
};

// Organization structured data
export const OrganizationStructuredData = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Borsamex",
  "url": "https://borsamex.com",
  "logo": "https://borsamex.com/logo512.png",
  "description": "Türkiye'nin AI destekli trading sinyal platformu",
  "contactPoint": {
    "@type": "ContactPoint",
    "contactType": "customer service",
    "email": "info@borsamex.com",
    "availableLanguage": "Turkish"
  },
  "sameAs": [
    "https://twitter.com/borsamex",
    "https://t.me/BorsamexSignalBot"
  ]
};

// FAQ structured data for How It Works page
export const FAQStructuredData = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Trading sinyali ne demek?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Sinyal, bir kripto paranın alınması veya satılması için yapılan öneridir. Her sinyalde giriş fiyatı, hedef fiyat(lar) ve zarar kes seviyesi belirtilir."
      }
    },
    {
      "@type": "Question",
      "name": "Borsamex nasıl çalışır?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yapay zeka algoritmaları 7/24 piyasaları tarar. Fırsat tespit edildiğinde AL veya SAT sinyali oluşturulur. Sinyal telefonunuza ulaşır, işlemi açmak size kalmış."
      }
    },
    {
      "@type": "Question",
      "name": "Borsama bağlanıyor musunuz?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Hayır! Borsamex sadece sinyal verir. Borsanıza bağlanmıyoruz, API key istemiyoruz, paranıza dokunmuyoruz. İşlemi kendi hesabınızda kendiniz yaparsınız."
      }
    },
    {
      "@type": "Question",
      "name": "Ücretsiz plan ile ne yapabilirim?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Ücretsiz plan ile tüm canlı sinyalleri görebilir, hedef seviyeleri (TP1, TP2, TP3) takip edebilir ve geçmiş performansı inceleyebilirsiniz. Anlık bildirimler Pro üyelik gerektirir."
      }
    }
  ]
};

export default SEO;
